import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { API_URL, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import {
  MessageSquare,
  Send,
  ArrowLeft,
  Loader2,
  User,
  Building2,
  Inbox,
  CheckCircle2,
  Clock,
} from 'lucide-react';

const MessagesPanel = ({ userType }) => {
  const [view, setView] = useState('inbox'); // inbox, conversation, compose
  const [inbox, setInbox] = useState([]);
  const [conversations, setConversations] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchInbox();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchInbox = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/messages/inbox`, {
        headers: {  }
      });
      setInbox(response.data.messages || []);
      setUnreadCount(response.data.unread || 0);
      
      // Group messages by sender for conversation view
      const grouped = {};
      response.data.messages.forEach(msg => {
        const key = msg.sender_id;
        if (!grouped[key]) {
          grouped[key] = {
            user_id: msg.sender_id,
            user_name: msg.sender_name,
            user_type: msg.sender_type,
            last_message: msg,
            unread: !msg.is_read ? 1 : 0
          };
        } else {
          if (!msg.is_read) grouped[key].unread++;
        }
      });
      setConversations(grouped);
    } catch (error) {
      console.error('Error fetching inbox:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const openConversation = async (userId, userName, userTypeOther) => {
    setSelectedUser({ user_id: userId, name: userName, user_type: userTypeOther });
    setView('conversation');
    setLoading(true);
    
    try {
      const response = await axios.get(`${API_URL}/messages/conversation/${userId}`, {
        headers: {  }
      });
      setMessages(response.data.messages || []);
      // Refresh inbox to update unread counts
      fetchInbox();
    } catch (error) {
      console.error('Error fetching conversation:', error);
      toast.error('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;

    setSending(true);
    try {
      await axios.post(
        `${API_URL}/messages`,
        {
          recipient_id: selectedUser.user_id,
          subject: 'Re: Conversation',
          content: newMessage
        },
        { headers: {  } }
      );
      
      setNewMessage('');
      // Refresh conversation
      openConversation(selectedUser.user_id, selectedUser.name, selectedUser.user_type);
      toast.success('Message sent!');
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to send message';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const getTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return formatDate(dateStr);
  };

  if (loading && view === 'inbox') {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  // Inbox View
  if (view === 'inbox') {
    const conversationList = Object.values(conversations);
    
    return (
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-gray-900">Messages</h3>
            {unreadCount > 0 && (
              <Badge className="bg-red-500 text-white text-xs">
                {unreadCount} new
              </Badge>
            )}
          </div>
        </div>
        
        {conversationList.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {conversationList.map((conv) => (
              <button
                key={conv.user_id}
                onClick={() => openConversation(conv.user_id, conv.user_name, conv.user_type)}
                className="w-full p-4 hover:bg-gray-50 transition-colors text-left"
                data-testid={`conversation-${conv.user_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    conv.user_type === 'employer' ? 'bg-blue-100' : 'bg-green-100'
                  }`}>
                    {conv.user_type === 'employer' ? (
                      <Building2 className="w-5 h-5 text-blue-600" />
                    ) : (
                      <User className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className={`font-medium ${conv.unread > 0 ? 'text-gray-900' : 'text-gray-700'}`}>
                        {conv.user_name}
                      </p>
                      <span className="text-xs text-gray-500">
                        {getTimeAgo(conv.last_message.created_at)}
                      </span>
                    </div>
                    <p className={`text-sm truncate ${conv.unread > 0 ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
                      {conv.last_message.content.substring(0, 60)}...
                    </p>
                    {conv.last_message.job_title && (
                      <p className="text-xs text-primary mt-1">
                        Re: {conv.last_message.job_title}
                      </p>
                    )}
                  </div>
                  {conv.unread > 0 && (
                    <Badge className="bg-primary text-white text-xs">
                      {conv.unread}
                    </Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No messages yet</p>
            <p className="text-sm text-gray-400 mt-1">
              {userType === 'employer' 
                ? 'Messages from candidates will appear here'
                : 'Messages from employers will appear here'}
            </p>
          </div>
        )}
      </div>
    );
  }

  // Conversation View
  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden flex flex-col h-[500px]">
      {/* Header */}
      <div className="p-4 border-b border-gray-100 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            setView('inbox');
            setSelectedUser(null);
            setMessages([]);
          }}
          data-testid="back-to-inbox"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
          selectedUser?.user_type === 'employer' ? 'bg-blue-100' : 'bg-green-100'
        }`}>
          {selectedUser?.user_type === 'employer' ? (
            <Building2 className="w-5 h-5 text-blue-600" />
          ) : (
            <User className="w-5 h-5 text-green-600" />
          )}
        </div>
        <div>
          <p className="font-semibold text-gray-900">{selectedUser?.name}</p>
          <p className="text-xs text-gray-500 capitalize">{selectedUser?.user_type?.replace('_', ' ')}</p>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-primary animate-spin" />
          </div>
        ) : messages.length > 0 ? (
          <div className="space-y-4">
            {messages.map((msg) => {
              const isOwn = msg.sender_id !== selectedUser?.user_id;
              return (
                <div
                  key={msg.message_id}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[75%] rounded-xl p-3 ${
                    isOwn 
                      ? 'bg-primary text-white rounded-br-sm' 
                      : 'bg-gray-100 text-gray-900 rounded-bl-sm'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    <div className={`flex items-center gap-1 mt-1 text-xs ${
                      isOwn ? 'text-white/70' : 'text-gray-500'
                    }`}>
                      <Clock className="w-3 h-3" />
                      {getTimeAgo(msg.created_at)}
                      {isOwn && msg.is_read && (
                        <CheckCircle2 className="w-3 h-3 ml-1" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No messages in this conversation</p>
          </div>
        )}
      </ScrollArea>

      {/* Message Input */}
      <form onSubmit={sendMessage} className="p-4 border-t border-gray-100">
        <div className="flex gap-2">
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 resize-none min-h-[60px] max-h-[120px]"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage(e);
              }
            }}
            data-testid="message-input"
          />
          <Button
            type="submit"
            disabled={sending || !newMessage.trim()}
            className="bg-primary hover:bg-primary-hover text-white self-end"
            data-testid="send-message-btn"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default MessagesPanel;
